/**
 * Theme Token Contract and Preset Definitions
 * All colors are in hex format for easier management and converted to CSS variables.
 */

export interface ThemeTokens {
  // Base Surfaces
  appBg: string;
  card: string;
  cardHover: string;
  mutedSurface: string;
  
  // Borders
  borderSubtle: string;
  borderStrong: string;
  
  // Typography
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  
  // Brand / Accent
  brand: string;
  brandHover: string;
  accent: string;
  
  // Status
  success: string;
  warning: string;
  danger: string;
  
  // Focus / Ring
  focus: string;
  
  // Skill Colors (for charts)
  skillConfidence: string;
  skillObjection: string;
  skillRapport: string;
  skillAppointment: string;
  
  // Shadows (CSS values)
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  tagline: string;
  idealFor: string;
  tokens: {
    light: ThemeTokens;
    dark: ThemeTokens;
  };
}

// Helper to generate dark mode shadows
const darkShadows = {
  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)',
  shadowMd: '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)',
  shadowLg: '0 12px 32px rgba(0, 0, 0, 0.5), 0 4px 8px rgba(0, 0, 0, 0.3)',
};

// Default RepRev theme (matches current index.css)
const defaultPreset: ThemePreset = {
  id: 'default',
  name: 'RepRev Default',
  description: 'The classic RepRev look with automotive blue and electric cyan accents.',
  tagline: 'Classic & Professional',
  idealFor: 'All dealerships',
  tokens: {
    light: {
      appBg: '#ECF0F3',
      card: '#F5F7FA',
      cardHover: '#FFFFFF',
      mutedSurface: '#E2E6EA',
      borderSubtle: '#D8DCE0',
      borderStrong: '#C4C9D0',
      textPrimary: '#0F172A',
      textSecondary: '#334155',
      textMuted: '#5c6675',
      brand: '#2f6fed',
      brandHover: '#255ed0',
      accent: '#7c5ce4',
      success: '#2f9e67',
      warning: '#d98722',
      danger: '#d14343',
      focus: '#2f6fed',
      skillConfidence: '#f97316',
      skillObjection: '#3b82f6',
      skillRapport: '#a855f7',
      skillAppointment: '#10b981',
      shadowSm: '0 1px 2px rgba(12,18,30,0.06), 0 1px 3px rgba(12,18,30,0.1)',
      shadowMd: '0 4px 12px rgba(12,18,30,0.08), 0 2px 4px rgba(12,18,30,0.04)',
      shadowLg: '0 12px 32px rgba(12,18,30,0.12), 0 4px 8px rgba(12,18,30,0.04)',
    },
    dark: {
      appBg: '#0d1117',
      card: '#161b22',
      cardHover: '#1c2128',
      mutedSurface: '#21262d',
      borderSubtle: '#30363d',
      borderStrong: '#484f58',
      textPrimary: '#f0f6fc',
      textSecondary: '#8b949e',
      textMuted: '#6e7681',
      brand: '#58a6ff',
      brandHover: '#79b8ff',
      accent: '#a371f7',
      success: '#3fb950',
      warning: '#d29922',
      danger: '#f85149',
      focus: '#58a6ff',
      skillConfidence: '#fb923c',
      skillObjection: '#60a5fa',
      skillRapport: '#c084fc',
      skillAppointment: '#34d399',
      ...darkShadows,
    },
  },
};

// 1. Velvet Red - Bold, energetic, Toyota-inspired
const velvetRedPreset: ThemePreset = {
  id: 'velvet-red',
  name: 'Velvet Red',
  description: 'Bold and energetic with a sports-car vibe. Deep velvet red creates a powerful, competitive atmosphere.',
  tagline: 'Bold & Energetic',
  idealFor: 'High-volume mainstream dealerships (Toyota, Kia, Hyundai)',
  tokens: {
    light: {
      appBg: '#F9F5F5',
      card: '#FFF8F8',
      cardHover: '#FFFFFF',
      mutedSurface: '#F3E9E9',
      borderSubtle: '#EADCDC',
      borderStrong: '#D5BABA',
      textPrimary: '#1E0A0A',
      textSecondary: '#5B2A2A',
      textMuted: '#8C5A5A',
      brand: '#990000',
      brandHover: '#B30000',
      accent: '#FF6600',
      success: '#2F9E67',
      warning: '#D98722',
      danger: '#CC3333',
      focus: '#990000',
      skillConfidence: '#dc2626',
      skillObjection: '#7c3aed',
      skillRapport: '#db2777',
      skillAppointment: '#059669',
      shadowSm: '0 1px 3px rgba(30,10,10,0.08)',
      shadowMd: '0 12px 30px rgba(30,10,10,0.12)',
      shadowLg: '0 24px 60px rgba(30,10,10,0.16)',
    },
    dark: {
      appBg: '#140808',
      card: '#1c0d0d',
      cardHover: '#241212',
      mutedSurface: '#2a1515',
      borderSubtle: '#3d2020',
      borderStrong: '#5c3030',
      textPrimary: '#faf5f5',
      textSecondary: '#c9a0a0',
      textMuted: '#8c6060',
      brand: '#ff4d4d',
      brandHover: '#ff6666',
      accent: '#ff8533',
      success: '#3fb950',
      warning: '#d29922',
      danger: '#ff6b6b',
      focus: '#ff4d4d',
      skillConfidence: '#f87171',
      skillObjection: '#a78bfa',
      skillRapport: '#f472b6',
      skillAppointment: '#34d399',
      ...darkShadows,
    },
  },
};

// 2. Midnight Neon - Futuristic, high-energy
const midnightNeonPreset: ThemePreset = {
  id: 'midnight-neon',
  name: 'Midnight Neon',
  description: 'Futuristic and high-energy with electric blue and neon purple. Feels like a gaming console.',
  tagline: 'Futuristic & High-Energy',
  idealFor: 'Young, tech-focused dealerships or performance divisions',
  tokens: {
    light: {
      appBg: '#F0F4FF',
      card: '#F8FAFF',
      cardHover: '#FFFFFF',
      mutedSurface: '#E4EBFF',
      borderSubtle: '#D0DBFF',
      borderStrong: '#A8BFFF',
      textPrimary: '#0A0E23',
      textSecondary: '#3D4A7A',
      textMuted: '#6E7A9C',
      brand: '#00BFFF',
      brandHover: '#32CDFF',
      accent: '#9A00FF',
      success: '#20C997',
      warning: '#FFA500',
      danger: '#E63946',
      focus: '#00BFFF',
      skillConfidence: '#f97316',
      skillObjection: '#06b6d4',
      skillRapport: '#d946ef',
      skillAppointment: '#22c55e',
      shadowSm: '0 1px 3px rgba(10,14,35,0.08)',
      shadowMd: '0 12px 30px rgba(10,14,35,0.15)',
      shadowLg: '0 24px 60px rgba(10,14,35,0.20)',
    },
    dark: {
      appBg: '#0A0E23',
      card: '#11162F',
      cardHover: '#1A2141',
      mutedSurface: '#0D1330',
      borderSubtle: '#1D2347',
      borderStrong: '#313A6A',
      textPrimary: '#F4F6FA',
      textSecondary: '#9DA8D1',
      textMuted: '#6E7A9C',
      brand: '#00BFFF',
      brandHover: '#32CDFF',
      accent: '#9A00FF',
      success: '#20C997',
      warning: '#FFA500',
      danger: '#E63946',
      focus: '#00BFFF',
      skillConfidence: '#fb923c',
      skillObjection: '#22d3ee',
      skillRapport: '#e879f9',
      skillAppointment: '#4ade80',
      shadowSm: '0 1px 3px rgba(0,191,255,0.1)',
      shadowMd: '0 12px 30px rgba(0,191,255,0.15)',
      shadowLg: '0 24px 60px rgba(0,191,255,0.20)',
    },
  },
};

// 3. Eco Sage - Calm, growth-minded
const ecoSagePreset: ThemePreset = {
  id: 'eco-sage',
  name: 'Eco Sage',
  description: 'Calm and growth-minded with forest and sage greens. Conveys trust and sustainability.',
  tagline: 'Calm & Growth-Minded',
  idealFor: 'EV dealers, sustainability-focused brands (Subaru, Volvo)',
  tokens: {
    light: {
      appBg: '#F4F7F3',
      card: '#FAFCF8',
      cardHover: '#FFFFFF',
      mutedSurface: '#EDF2EB',
      borderSubtle: '#D7E0D1',
      borderStrong: '#B9C7AC',
      textPrimary: '#0F2411',
      textSecondary: '#385538',
      textMuted: '#5E725B',
      brand: '#2F855A',
      brandHover: '#246946',
      accent: '#7BB662',
      success: '#38A169',
      warning: '#D69E2E',
      danger: '#B33A3A',
      focus: '#2F855A',
      skillConfidence: '#ca8a04',
      skillObjection: '#0891b2',
      skillRapport: '#7c3aed',
      skillAppointment: '#16a34a',
      shadowSm: '0 1px 3px rgba(15,36,17,0.08)',
      shadowMd: '0 12px 30px rgba(15,36,17,0.12)',
      shadowLg: '0 24px 60px rgba(15,36,17,0.16)',
    },
    dark: {
      appBg: '#0d1410',
      card: '#131c16',
      cardHover: '#1a251c',
      mutedSurface: '#1e2a20',
      borderSubtle: '#2d3f30',
      borderStrong: '#4a6350',
      textPrimary: '#f4f7f3',
      textSecondary: '#a8c4a0',
      textMuted: '#6e8a68',
      brand: '#48bb78',
      brandHover: '#68d391',
      accent: '#9ae6b4',
      success: '#48bb78',
      warning: '#ecc94b',
      danger: '#fc8181',
      focus: '#48bb78',
      skillConfidence: '#facc15',
      skillObjection: '#22d3ee',
      skillRapport: '#a78bfa',
      skillAppointment: '#4ade80',
      ...darkShadows,
    },
  },
};

// 4. Luxury Minimal - Sophisticated, upscale
const luxuryMinimalPreset: ThemePreset = {
  id: 'luxury-minimal',
  name: 'Luxury Minimal',
  description: 'Sophisticated and upscale with deep navy and champagne gold. Minimal and elegant.',
  tagline: 'Sophisticated & Upscale',
  idealFor: 'High-end marques (Lexus, BMW, Mercedes)',
  tokens: {
    light: {
      appBg: '#F7F6F2',
      card: '#FFFFFF',
      cardHover: '#FAFAFA',
      mutedSurface: '#EFEDE8',
      borderSubtle: '#E1E0DB',
      borderStrong: '#C9C8C0',
      textPrimary: '#0D0C0C',
      textSecondary: '#363432',
      textMuted: '#6B6862',
      brand: '#0F3D5A',
      brandHover: '#154D74',
      accent: '#AE8A55',
      success: '#2F9E67',
      warning: '#D4A017',
      danger: '#B03644',
      focus: '#0F3D5A',
      skillConfidence: '#b45309',
      skillObjection: '#1d4ed8',
      skillRapport: '#7e22ce',
      skillAppointment: '#15803d',
      shadowSm: '0 1px 3px rgba(13,12,12,0.06)',
      shadowMd: '0 12px 30px rgba(13,12,12,0.10)',
      shadowLg: '0 24px 60px rgba(13,12,12,0.14)',
    },
    dark: {
      appBg: '#0c0c0b',
      card: '#141413',
      cardHover: '#1c1c1a',
      mutedSurface: '#1f1f1d',
      borderSubtle: '#2d2d2a',
      borderStrong: '#454540',
      textPrimary: '#f7f6f2',
      textSecondary: '#a8a7a0',
      textMuted: '#6b6a64',
      brand: '#5a9fd4',
      brandHover: '#7ab5e0',
      accent: '#d4b896',
      success: '#3fb950',
      warning: '#e6c84b',
      danger: '#d46a6a',
      focus: '#5a9fd4',
      skillConfidence: '#d97706',
      skillObjection: '#3b82f6',
      skillRapport: '#a855f7',
      skillAppointment: '#22c55e',
      ...darkShadows,
    },
  },
};

// 5. Performance Black - Modern, stealthy
const performanceBlackPreset: ThemePreset = {
  id: 'performance-black',
  name: 'Performance Black',
  description: 'Modern and stealthy with warm gold accents on dark surfaces. Premium and techy.',
  tagline: 'Modern & Stealthy',
  idealFor: 'Aftermarket performance shops, tuner brands, supercar divisions',
  tokens: {
    light: {
      appBg: '#F5F5F7',
      card: '#FFFFFF',
      cardHover: '#FAFAFA',
      mutedSurface: '#EBEBED',
      borderSubtle: '#DCDCE0',
      borderStrong: '#C4C4CA',
      textPrimary: '#0F1014',
      textSecondary: '#45464D',
      textMuted: '#6D7280',
      brand: '#C9A227',
      brandHover: '#DDB82E',
      accent: '#EF476F',
      success: '#06D6A0',
      warning: '#FFD166',
      danger: '#EF476F',
      focus: '#C9A227',
      skillConfidence: '#eab308',
      skillObjection: '#6366f1',
      skillRapport: '#ec4899',
      skillAppointment: '#14b8a6',
      shadowSm: '0 1px 3px rgba(15,16,20,0.08)',
      shadowMd: '0 12px 30px rgba(15,16,20,0.12)',
      shadowLg: '0 24px 60px rgba(15,16,20,0.16)',
    },
    dark: {
      appBg: '#0F1014',
      card: '#15171C',
      cardHover: '#1C1E24',
      mutedSurface: '#1A1B21',
      borderSubtle: '#2B2D36',
      borderStrong: '#434751',
      textPrimary: '#F5F7FA',
      textSecondary: '#A3A8B4',
      textMuted: '#6D7280',
      brand: '#E8C547',
      brandHover: '#FFD369',
      accent: '#EF476F',
      success: '#06D6A0',
      warning: '#FFD166',
      danger: '#EF476F',
      focus: '#E8C547',
      skillConfidence: '#fbbf24',
      skillObjection: '#818cf8',
      skillRapport: '#f472b6',
      skillAppointment: '#2dd4bf',
      shadowSm: '0 1px 3px rgba(232,197,71,0.08)',
      shadowMd: '0 12px 30px rgba(232,197,71,0.12)',
      shadowLg: '0 24px 60px rgba(232,197,71,0.16)',
    },
  },
};

// 6. Horizon Blue - Calm, trustworthy
const horizonBluePreset: ThemePreset = {
  id: 'horizon-blue',
  name: 'Horizon Blue',
  description: 'Calm and trustworthy with denim and sky blue. Professional and reliable.',
  tagline: 'Calm & Trustworthy',
  idealFor: 'Fleet managers, B2B sales, commercial vehicles',
  tokens: {
    light: {
      appBg: '#F3F7FC',
      card: '#FBFDFF',
      cardHover: '#FFFFFF',
      mutedSurface: '#E6EFF8',
      borderSubtle: '#D0DCEC',
      borderStrong: '#B4C5DB',
      textPrimary: '#102A43',
      textSecondary: '#486581',
      textMuted: '#7C94B0',
      brand: '#2A6FAE',
      brandHover: '#215A8C',
      accent: '#5DADE2',
      success: '#2ECC71',
      warning: '#F1C40F',
      danger: '#E74C3C',
      focus: '#2A6FAE',
      skillConfidence: '#ea580c',
      skillObjection: '#0284c7',
      skillRapport: '#9333ea',
      skillAppointment: '#16a34a',
      shadowSm: '0 1px 3px rgba(16,42,67,0.08)',
      shadowMd: '0 12px 30px rgba(16,42,67,0.12)',
      shadowLg: '0 24px 60px rgba(16,42,67,0.16)',
    },
    dark: {
      appBg: '#0a1420',
      card: '#0f1c2c',
      cardHover: '#152536',
      mutedSurface: '#1a2d42',
      borderSubtle: '#2a4560',
      borderStrong: '#3d6080',
      textPrimary: '#f3f7fc',
      textSecondary: '#94b8d8',
      textMuted: '#6890b0',
      brand: '#5a9fd4',
      brandHover: '#7ab5e0',
      accent: '#85c5f0',
      success: '#4cd964',
      warning: '#f5d547',
      danger: '#ff6b6b',
      focus: '#5a9fd4',
      skillConfidence: '#fb923c',
      skillObjection: '#38bdf8',
      skillRapport: '#c084fc',
      skillAppointment: '#4ade80',
      ...darkShadows,
    },
  },
};

// 7. Sunrise Amber - Warm, inviting, friendly
const sunriseAmberPreset: ThemePreset = {
  id: 'sunrise-amber',
  name: 'Sunrise Amber',
  description: 'Warm and inviting with burnt orange and peach tones. Creates a welcoming feel.',
  tagline: 'Warm & Inviting',
  idealFor: 'Family-owned stores, customer-service-oriented dealerships',
  tokens: {
    light: {
      appBg: '#FFFAF5',
      card: '#FFFCF9',
      cardHover: '#FFFFFF',
      mutedSurface: '#FFF5EB',
      borderSubtle: '#F7E9DE',
      borderStrong: '#E8D0BA',
      textPrimary: '#4A2E19',
      textSecondary: '#7A5038',
      textMuted: '#A77959',
      brand: '#E28D42',
      brandHover: '#CC7831',
      accent: '#F1C27D',
      success: '#2F9E67',
      warning: '#C58A0D',
      danger: '#B33A3A',
      focus: '#E28D42',
      skillConfidence: '#dc2626',
      skillObjection: '#2563eb',
      skillRapport: '#c026d3',
      skillAppointment: '#059669',
      shadowSm: '0 1px 3px rgba(74,46,25,0.08)',
      shadowMd: '0 12px 30px rgba(74,46,25,0.12)',
      shadowLg: '0 24px 60px rgba(74,46,25,0.16)',
    },
    dark: {
      appBg: '#14100c',
      card: '#1c1612',
      cardHover: '#241c16',
      mutedSurface: '#2a211a',
      borderSubtle: '#403020',
      borderStrong: '#604830',
      textPrimary: '#fff8f2',
      textSecondary: '#d4b090',
      textMuted: '#a08060',
      brand: '#f0a050',
      brandHover: '#f5b570',
      accent: '#f8d4a0',
      success: '#4cd964',
      warning: '#e6b020',
      danger: '#f07070',
      focus: '#f0a050',
      skillConfidence: '#f87171',
      skillObjection: '#60a5fa',
      skillRapport: '#e879f9',
      skillAppointment: '#34d399',
      ...darkShadows,
    },
  },
};

// Export all presets
export const THEME_PRESETS: ThemePreset[] = [
  defaultPreset,
  velvetRedPreset,
  midnightNeonPreset,
  ecoSagePreset,
  luxuryMinimalPreset,
  performanceBlackPreset,
  horizonBluePreset,
  sunriseAmberPreset,
];

// Helper to get preset by ID
export const getPresetById = (id: string): ThemePreset => {
  return THEME_PRESETS.find(p => p.id === id) || defaultPreset;
};

// Helper to merge preset tokens with overrides
export const mergeTokens = (
  preset: ThemePreset,
  overrides: Partial<ThemeTokens>,
  mode: 'light' | 'dark'
): ThemeTokens => {
  return {
    ...preset.tokens[mode],
    ...overrides,
  };
};

// CSS variable mapping - maps token keys to CSS variable names
export const TOKEN_TO_CSS_VAR: Record<keyof ThemeTokens, string> = {
  appBg: '--background',
  card: '--card',
  cardHover: '--card-hover',
  mutedSurface: '--muted',
  borderSubtle: '--border',
  borderStrong: '--border-strong',
  textPrimary: '--foreground',
  textSecondary: '--secondary-foreground',
  textMuted: '--muted-foreground',
  brand: '--primary',
  brandHover: '--primary-hover',
  accent: '--accent',
  success: '--success',
  warning: '--warning',
  danger: '--destructive',
  focus: '--ring',
  skillConfidence: '--skill-confidence',
  skillObjection: '--skill-objection',
  skillRapport: '--skill-rapport',
  skillAppointment: '--skill-appointment',
  shadowSm: '--shadow-sm',
  shadowMd: '--shadow-md',
  shadowLg: '--shadow-lg',
};

export type { ThemeTokens as ThemeTokensType };
